import { useState, useEffect, useCallback } from 'react'
import { fitThesisApi } from '@/lib/api/client'
import type { FitThesis, FitThesisBlockKey } from '@ispot/shared'

export function useFitTheses(accountId: string, projectId: string) {
  const [theses, setTheses] = useState<FitThesis[]>([])
  const [isLoading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    if (!accountId || !projectId) return
    setLoading(true)
    setError(null)
    try {
      const { theses } = await fitThesisApi.listTheses(accountId, projectId) as { theses: FitThesis[] }
      setTheses(theses)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load theses')
    } finally {
      setLoading(false)
    }
  }, [accountId, projectId])

  useEffect(() => { reload() }, [reload])

  const createThesis = useCallback(async (personaSessionId: string, jtbdId: string, painId?: string) => {
    const { thesis } = await fitThesisApi.createThesis(accountId, projectId, { personaSessionId, jtbdId, painId }) as { thesis: FitThesis }
    await reload()
    return thesis
  }, [accountId, projectId, reload])

  const patchBlock = useCallback(async (id: string, blockKey: FitThesisBlockKey, value: unknown) => {
    const { thesis } = await fitThesisApi.patchBlock(accountId, projectId, id, blockKey, value) as { thesis: FitThesis }
    setTheses(prev => prev.map(t => t.id === thesis.id ? thesis : t))
    return thesis
  }, [accountId, projectId])

  const deleteThesis = useCallback(async (id: string) => {
    await fitThesisApi.deleteThesis(accountId, projectId, id)
    setTheses(prev => prev.filter(t => t.id !== id))
  }, [accountId, projectId])

  const validateThesis = useCallback(async (id: string) => {
    const { thesis } = await fitThesisApi.validateThesis(accountId, projectId, id) as { thesis: FitThesis }
    setTheses(prev => prev.map(t => t.id === thesis.id ? thesis : t))
    return thesis
  }, [accountId, projectId])

  return { theses, isLoading, error, reload, createThesis, patchBlock, deleteThesis, validateThesis }
}
